import React, { useState } from 'react';
import { useNavigate } from "react-router-dom";
import styled from 'styled-components';
import DOMPurify from 'dompurify';
import useStore from '../store.js';
import LogoIcon from '../img/logo_chat.svg';
import SearchIcon from '../img/search.png';
import button from '../img/ask_button.png';
import ModalPayCheck from './Modal/ModalPayCheck.jsx';
import ModalPrecedentGuide from './Modal/ModalPrecedentGuide.jsx';
import { trackNaverSearch } from '../utils/naverConversion.js';
import { LowActionConflictError } from '../utils/lowAction.js';
import { resolveChatDisplayQuestion } from '../utils/chatQuestion.js';

const HistoryBackIcon = () => (
    <svg width="7" height="12" viewBox="0 0 7 12" fill="none" aria-hidden="true">
        <path d="M6 1L1 6L6 11" stroke="rgba(0,0,0,0.55)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const HistoryForwardIcon = () => (
    <svg width="7" height="12" viewBox="0 0 7 12" fill="none" aria-hidden="true">
        <path d="M1 1L6 6L1 11" stroke="rgba(0,0,0,0.55)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const UpdatedSearchSparkle = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
            d="M1.60357 7.87929C4.77046 7.24591 7.24591 4.77046 7.87929 1.60357L8 1L8.12071 1.60357C8.75409 4.77046 11.2295 7.24591 14.3964 7.87929L15 8L14.3964 8.12071C11.2295 8.75409 8.75409 11.2295 8.12071 14.3964L8 15L7.87929 14.3964C7.24591 11.2295 4.77046 8.75409 1.60357 8.12071L1 8L1.60357 7.87929Z"
            fill="url(#updated-search-sparkle)"
        />
        <defs>
            <linearGradient
                id="updated-search-sparkle"
                x1="5.69149"
                y1="11.7312"
                x2="13.1631"
                y2="11.7312"
                gradientUnits="userSpaceOnUse"
            >
                <stop stopColor="#1AE276" />
                <stop offset="0.0817308" stopColor="#1CDA81" />
                <stop offset="1" stopColor="#3A7DFF" />
            </linearGradient>
        </defs>
    </svg>
);

export const UpdatedSearchAction = (props) => (
    <UpdateButton {...props}>
        <UpdatedSearchSparkle />
        <span>업데이트 반영 검색</span>
        <span aria-hidden="true">→</span>
    </UpdateButton>
);

const ChatContainer = ({ guestMode = false, onRequestDirectInput }) => {
    const { 
        chatHistory, 
        isAIResponding, 
        isChatDone,
        updatedQuestion,
        updatedQuestionHistory,
        questionHistoryViewIndex,
        goQuestionHistoryBack,
        goQuestionHistoryForward,
        startSSE,
        setHasAttemptedQuestion,
        setTextValue,
        chatErrorMessage,
        chatSearchFallbackAvailable,
        guestQueueMessage,
        guestSessionComplete,
        isLoading,
        answerChatQuestion,
        requestGuestLogin,
        resetChatPaid
    } = useStore();
    const navigate = useNavigate();
    const [showPayCheckModal, setShowPayCheckModal] = useState(false);
    const [showResetModal, setShowResetModal] = useState(false);
    const [showPaidSearchModal, setShowPaidSearchModal] = useState(false);
    const [showPrecedentGuideModal, setShowPrecedentGuideModal] = useState(false);
    const [extractedCaseInfo, setExtractedCaseInfo] = useState({ courtName: '', caseNumber: '' });

    // 5로우 차감 대화 초기화 확인
    const handleConfirmReset = async () => {
        try {
            const success = await resetChatPaid();
            if (success) {
                setShowResetModal(false);
            } else {
                alert('대화 초기화에 실패했습니다. 로우가 부족한지 확인해 주세요.');
            }
        } catch (error) {
            if (error instanceof LowActionConflictError) return;
            alert(error.message || '대화 초기화 중 오류가 발생했습니다.');
        }
    };

    // 판례번호 검색 응답인지 확인하는 함수
    const isPrecedentSearchResponse = (content) => {
        if (!content) return false;
        // "판례번호" + "검색" + "준비" 같은 키워드 조합으로 감지
        const hasPrecedentNumber = content.includes('판례번호') || content.includes('판례 번호');
        const hasSearch = content.includes('검색');
        const isNotReady = content.includes('준비 중') || content.includes('준비중');
        
        return hasPrecedentNumber && hasSearch && isNotReady;
    };

    // updatedQuestion에서 판례번호와 법원 정보 추출
    const extractCaseInfo = (text) => {
        if (!text) return { courtName: '', caseNumber: '' };
        
        // 판례번호 패턴: 4자리 연도 + 한글 1~4자 + 숫자 (예: 2014고정1065, 2022나210995)
        // \b를 사용하여 단어 경계 확인
        const caseNumberPattern = /\b(\d{4}[가-힣]{1,4}\d+)\b/;
        const caseNumberMatch = text.match(caseNumberPattern);
        const caseNumber = caseNumberMatch ? caseNumberMatch[1] : '';

        // 법원 정보 추출 (예: "대법원", "서울고등법원", "부산지방법원" 등)
        const courtPattern = /(대법원|[가-힣]{2,}고등법원|[가-힣]{2,}지방법원|[가-힣]{2,}법원)/;
        const courtMatch = text.match(courtPattern);
        const courtName = courtMatch ? courtMatch[1] : '';

        return { courtName, caseNumber };
    };

    // 마크다운을 HTML로 변환하고 DOMPurify로 새니타이징하는 함수
    const convertMarkdownToHtml = (text) => {
        if (!text) return '';
        
        let convertedText = text;
        
        // **볼드체** → <strong>볼드체</strong>
        convertedText = convertedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        
        // 추가 마크다운 지원 (필요시 확장 가능)
        // *이탤릭* → <em>이탤릭</em>
        // convertedText = convertedText.replace(/\*(.*?)\*/g, '<em>$1</em>');
        
        // `코드` → <code>코드</code>
        // convertedText = convertedText.replace(/`(.*?)`/g, '<code>$1</code>');
        
        // 줄바꿈 처리
        convertedText = convertedText.replace(/\n/g, '<br />');
        
        // DOMPurify로 새니타이징 (XSS 공격 방지)
        return DOMPurify.sanitize(convertedText, {
            ALLOWED_TAGS: ['strong', 'br'], // 현재 사용하는 태그만 허용
            ALLOWED_ATTR: [] // 허용할 속성 없음
        });
    };

    // AI 응답이 로딩 중인지 확인
    const shouldShowLoading = () => {
        if (!isAIResponding) return false;
        
        const userMessages = chatHistory.filter(msg => msg.type === 'user');
        const aiMessages = chatHistory.filter(msg => msg.type === 'ai');
        
        return userMessages.length > aiMessages.length;
    };

    // 판례 검색 버튼 클릭 - 10로우 차감 모달 표시
    const handleSearchCase = () => {
        if (guestMode) {
            requestGuestLogin("search");
            return;
        }
        setShowPayCheckModal(true);
    };

    const handleDirectInput = () => {
        if (guestMode && guestSessionComplete) {
            requestGuestLogin("continue");
            return;
        }
        onRequestDirectInput?.();
    };

    const getCurrentSearchText = () => {
        return resolveChatDisplayQuestion({
            updatedQuestion,
            updatedQuestionHistory,
            questionHistoryViewIndex,
            chatHistory,
        });
    };

    const runCaseSearch = (searchText) => {
        if (!searchText.trim()) return false;

        // Chat에서 판례 검색 시작임을 표시 (새로고침 감지 방지용)
        sessionStorage.setItem("fromChatSearch", "true");
        
        setHasAttemptedQuestion();
        setTextValue(searchText);
        trackNaverSearch();
        
        const handleComplete = (newLogId) => {
            navigate(`/main/q/${newLogId}`, { replace: true });
        };

        const handleSSEStart = () => {
            navigate(`/main/q/waiting`);
        };

        startSSE(searchText, handleComplete, handleSSEStart);
        return true;
    };

    const handlePaidSearchCase = () => {
        if (!getCurrentSearchText().trim()) return;
        if (guestMode) {
            requestGuestLogin("search");
            return;
        }
        setShowPaidSearchModal(true);
    };

    // 원문검색 가이드 보기 버튼 클릭
    const handleOpenPrecedentGuide = () => {
        if (guestMode) {
            requestGuestLogin("search");
            return;
        }
        let finalCaseInfo = { courtName: '', caseNumber: '' };
        
        // 1. updatedQuestion에서 판례번호와 법원 정보 추출 시도
        if (updatedQuestion && updatedQuestion.trim()) {
            const caseInfo = extractCaseInfo(updatedQuestion);
            if (caseInfo.caseNumber) {
                finalCaseInfo = caseInfo;
            }
        }
        
        // 2. 추출 실패 시 사용자 메시지에서 시도
        if (!finalCaseInfo.caseNumber) {
            const userMessages = chatHistory.filter(msg => msg.type === 'user');
            if (userMessages.length > 0) {
                const lastUserMessage = userMessages[userMessages.length - 1].content;
                const fallbackInfo = extractCaseInfo(lastUserMessage);
                if (fallbackInfo.caseNumber) {
                    finalCaseInfo = fallbackInfo;
                }
            }
        }
        
        // 3. 여전히 추출 실패 시 기본값 사용
        if (!finalCaseInfo.caseNumber) {
            finalCaseInfo = {
                courtName: '부산고등법원',
                caseNumber: '2022나210995'
            };
        }
        
        setExtractedCaseInfo(finalCaseInfo);
        setShowPrecedentGuideModal(true);
    };

    // 10로우 차감 확인 후 실제 검색 실행
    const handleConfirmSearch = () => {
        // 가장 최신 사용자 메시지 또는 업데이트된 질문 사용
        const searchText = getCurrentSearchText();
        if (runCaseSearch(searchText)) {
            setShowPayCheckModal(false);
        }
    };

    const handleConfirmPaidSearch = async () => {
        const searchText = getCurrentSearchText();
        if (!searchText.trim()) return;

        try {
            const success = await resetChatPaid({ preserveLocalState: true });
            if (success) {
                setShowPaidSearchModal(false);
                runCaseSearch(searchText);
            } else {
                alert('대화 초기화에 실패했습니다. 로우가 부족한지 확인해 주세요.');
            }
        } catch (error) {
            if (error instanceof LowActionConflictError) return;
            alert(error.message || '대화 초기화 중 오류가 발생했습니다.');
        }
    };

    // 가장 마지막 AI 응답 메시지의 인덱스 찾기
    const lastAIMessageIndex = chatHistory.reduce((lastIndex, message, index) => {
        return message.type === 'ai' ? index : lastIndex;
    }, -1);

    const cardPreviewText = resolveChatDisplayQuestion({
        updatedQuestion,
        updatedQuestionHistory,
        questionHistoryViewIndex,
        chatHistory,
    });
    const hasSearchHistoryCard = Boolean(cardPreviewText && String(cardPreviewText).trim());

    const questionHistoryLen = updatedQuestionHistory.length;
    const questionHistorySafeIdx =
      questionHistoryLen === 0
        ? 0
        : Math.min(Math.max(0, questionHistoryViewIndex), questionHistoryLen - 1);
    const showQuestionHistoryNav = isChatDone && questionHistoryLen > 0;
    const shouldShowSearchButton = isChatDone && hasSearchHistoryCard;
    const shouldShowPaidSearchButton = Boolean(
        chatErrorMessage &&
        getCurrentSearchText().trim() &&
        (chatErrorMessage.includes('소진') || chatErrorMessage.includes('초과') || chatErrorMessage.includes('횟수'))
    );
    const shouldShowErrorSearchButton = Boolean(
        chatErrorMessage &&
        chatSearchFallbackAvailable &&
        getCurrentSearchText().trim() &&
        !shouldShowPaidSearchButton
    );

    return (
        <>
        <Wrapper>
            {chatHistory.map((message, index) => {
                // 마지막 AI 메시지인지 확인
                const isLastAIMessage = message.type === 'ai' && index === lastAIMessageIndex;
                const isActiveMessage = isLastAIMessage && index === chatHistory.length - 1;
                
                // 판례번호 검색 응답이면 무조건 버튼 표시 (updatedQuestion 조건 없이)
                const shouldShowPrecedentGuideButton = isLastAIMessage && isPrecedentSearchResponse(message.content);
                
                // 일반 응답이고 updatedQuestion이 있으면 업데이트 반영 검색 버튼 표시
                const shouldShowUpdateButton = isLastAIMessage && !isChatDone &&
                                              !chatErrorMessage &&
                                              !isPrecedentSearchResponse(message.content) && 
                                              hasSearchHistoryCard;
                const hasAnswerChoices = message.type === 'ai' &&
                    message.questionId && message.answerOptions?.length >= 2;
                const isSearchReadyMessage = message.type === 'ai' &&
                    ((isLastAIMessage && isChatDone) || message.content?.includes('검색 준비가 완료'));
                const hasActionChoices = message.type === 'ai' &&
                    (isSearchReadyMessage || !hasAnswerChoices) && !isPrecedentSearchResponse(message.content);
                const actionsDisabled = !isActiveMessage || isAIResponding || isLoading || Boolean(chatErrorMessage);
                const choicesDisabled = actionsDisabled || isChatDone || (guestMode && guestSessionComplete);
                
                return (
                    <MessageWrapper key={`${message.timestamp}-${index}`} $isUser={message.type === 'user'}>
                        {message.type === 'ai' && (
                            <LogoContainer>
                                <Logo src={LogoIcon} alt="로고" />
                            </LogoContainer>
                        )}
                        <MessageItem
                            $isUser={message.type === 'user'}
                            data-clarification-question={(hasAnswerChoices && !choicesDisabled) ||
                                (hasActionChoices && !actionsDisabled) ? '' : undefined}
                        >
                            {message.type === 'ai' ? (
                                <>
                                    <MessageText
                                        dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(message.content) }}
                                    />
                                    {hasAnswerChoices && (
                                        <AnswerChoices role="group" aria-label="추가 질문 답변 선택">
                                            {message.answerOptions.map((answer, optionIndex) => (
                                                <AnswerChoice
                                                    key={answer}
                                                    type="button"
                                                    data-behavior="clarification-option"
                                                    disabled={choicesDisabled}
                                                    onClick={() => answerChatQuestion(message.questionId, answer, { isGuest: guestMode })}
                                                >
                                                    <ChoiceNumber aria-hidden="true">{optionIndex + 1}</ChoiceNumber>
                                                    <span>{answer}</span>
                                                </AnswerChoice>
                                            ))}
                                            <ChoiceAlternatives>
                                                <AlternativeChoice
                                                    type="button"
                                                    disabled={choicesDisabled}
                                                    data-behavior="clarification-unknown"
                                                    onClick={() => answerChatQuestion(message.questionId, '모르겠어요', { isGuest: guestMode })}
                                                >모르겠어요</AlternativeChoice>
                                                <AlternativeChoice
                                                    type="button"
                                                    disabled={choicesDisabled}
                                                    data-behavior="clarification-direct"
                                                    onClick={handleDirectInput}
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                                                        <path d="m9.5 2 2.5 2.5-7 7H2.5V9L9.5 2Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
                                                        <path d="m8 3.5 2.5 2.5" stroke="currentColor" strokeWidth="1.2" />
                                                    </svg>
                                                    직접 입력
                                                </AlternativeChoice>
                                            </ChoiceAlternatives>
                                        </AnswerChoices>
                                    )}
                                    {hasActionChoices && (
                                        <AnswerChoices role="group" aria-label={isSearchReadyMessage ? "검색 준비 후 답변 선택" : "다음 답변 선택"}>
                                            {hasSearchHistoryCard && (
                                                <AnswerChoice
                                                    type="button"
                                                    disabled={actionsDisabled}
                                                    onClick={handleSearchCase}
                                                >{isSearchReadyMessage ? '이대로 검색할게요' : '현재 내용으로 검색할게요'}</AnswerChoice>
                                            )}
                                            <AnswerChoice
                                                type="button"
                                                disabled={actionsDisabled}
                                                onClick={handleDirectInput}
                                            >{isSearchReadyMessage ? '내용을 더 보탤게요' : '내용을 직접 입력할게요'}</AnswerChoice>
                                        </AnswerChoices>
                                    )}
                                    {shouldShowPrecedentGuideButton && (
                                        <PrecedentGuideButtonWrapper>
                                            <PrecedentGuideButton onClick={handleOpenPrecedentGuide} disabled={isAIResponding}>
                                                <PrecedentGuideButtonIcon src={SearchIcon} alt="" />
                                                원문검색 가이드 보기 →
                                            </PrecedentGuideButton>
                                        </PrecedentGuideButtonWrapper>
                                    )}
                                    {shouldShowUpdateButton && (
                                        <UpdateButtonWrapper>
                                            <UpdatedSearchAction
                                                onClick={handleSearchCase}
                                                disabled={isAIResponding}
                                            />
                                        </UpdateButtonWrapper>
                                    )}
                                </>
                            ) : (
                                <MessageText>
                                    {message.content}
                                </MessageText>
                            )}
                        </MessageItem>
                    </MessageWrapper>
                );
            })}
            
            {/* AI 응답 로딩 중이거나 에러 메시지 표시 */}
            {(shouldShowLoading() || chatErrorMessage || guestQueueMessage) && (
                <MessageWrapper $isUser={false}>
                    <LogoContainer>
                        <Logo src={LogoIcon} alt="로고" />
                    </LogoContainer>
                    <MessageItem $isUser={false}>
                        <MessageText>
                            {guestQueueMessage ? (
                                <QueueMessage>{guestQueueMessage}</QueueMessage>
                            ) : chatErrorMessage ? (
                                <>
                                    <ErrorMessage>{chatErrorMessage}</ErrorMessage>
                                    {shouldShowPaidSearchButton && (
                                        <PaidSearchButtonWrapper>
                                            <PaidSearchButton onClick={handlePaidSearchCase} disabled={isAIResponding}>
                                                이대로 판례 검색
                                            </PaidSearchButton>
                                        </PaidSearchButtonWrapper>
                                    )}
                                    {shouldShowErrorSearchButton && (
                                        <PaidSearchButtonWrapper>
                                            <PaidSearchButton onClick={handleSearchCase} disabled={isAIResponding}>
                                                현재 내용으로 판례 검색
                                            </PaidSearchButton>
                                        </PaidSearchButtonWrapper>
                                    )}
                                </>
                            ) : (
                                <ShimmerText>답변을 생성중입니다...</ShimmerText>
                            )}
                        </MessageText>
                    </MessageItem>
                </MessageWrapper>
            )}
            
        </Wrapper>

        {shouldShowSearchButton && (
            <SearchButtonSlot>
                <SearchButtonContainer>
                    <SearchNavLeft>
                        {showQuestionHistoryNav && (
                            <>
                                <HistoryRoundNavButton
                                    type="button"
                                    disabled={questionHistorySafeIdx <= 0}
                                    onClick={() => goQuestionHistoryBack()}
                                    aria-label="이전 질문 버전 보기"
                                >
                                    <HistoryBackIcon />
                                </HistoryRoundNavButton>
                                <HistoryStepText>
                                    {questionHistorySafeIdx + 1}/{questionHistoryLen}
                                </HistoryStepText>
                                <HistoryRoundNavButton
                                    type="button"
                                    disabled={questionHistorySafeIdx >= questionHistoryLen - 1}
                                    onClick={() => goQuestionHistoryForward()}
                                    aria-label="다음 질문 버전 보기"
                                >
                                    <HistoryForwardIcon />
                                </HistoryRoundNavButton>
                            </>
                        )}
                    </SearchNavLeft>
                    <SearchNavRight>
                        <ConfirmSearchText>네, 이대로 검색할게요</ConfirmSearchText>
                        <CircleArrowButton onClick={handleSearchCase} aria-label="판례 검색 진행" />
                    </SearchNavRight>
                </SearchButtonContainer>
            </SearchButtonSlot>
        )}
        
        {/* 10로우 차감 확인 모달 */}
        <ModalPayCheck
            isOpen={showPayCheckModal}
            onClose={() => {
                setShowPayCheckModal(false);
            }}
            onConfirm={handleConfirmSearch}
            law={10}
            buttonType="queryButton"
            headerContent="판례 검색하기"
        />

        {/* 5로우 차감 대화 초기화 확인 모달 */}
        <ModalPayCheck
            isOpen={showResetModal}
            onClose={() => {
                setShowResetModal(false);
            }}
            onConfirm={handleConfirmReset}
            law={5}
            buttonType="resetButton"
            headerContent="대화 초기화하기"
        />

        {/* 15로우 차감 후 대화 초기화 및 판례 검색 확인 모달 */}
        <ModalPayCheck
            isOpen={showPaidSearchModal}
            onClose={() => {
                setShowPaidSearchModal(false);
            }}
            onConfirm={handleConfirmPaidSearch}
            law={15}
            buttonType="resetButton"
            headerContent="판례 검색하기"
            additionalContent="5로우(대화 초기화) + 10로우(질문 검색)"
        />

        {/* 판례 원문검색 가이드 모달 */}
        <ModalPrecedentGuide
            isOpen={showPrecedentGuideModal}
            onClose={() => setShowPrecedentGuideModal(false)}
            courtName={extractedCaseInfo.courtName}
            caseNumber={extractedCaseInfo.caseNumber}
        />
    </>
    );
};

const Wrapper = styled.div`
  width: 680px;
  height: auto;
  flex: 0 0 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 16px;
  overflow: visible;

  @media (max-width: 1140px) and (min-width: 600px) {
    width: 536px;
  }

  @media (max-width: 599px) {
    width: calc(100% - 48px);
  }
`;

const MessageWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: ${props => props.$isUser ? 'flex-end' : 'flex-start'};
  gap: 8px;
  width: 100%;
  margin-bottom: 20px;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #F9F9F9;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 4px;
`;

const Logo = styled.img`
  width: 16px;
  height: 7.6px;
`;

const MessageItem = styled.div`
  min-width: 0;
  // max-width: 380px; 
  max-width: ${props => props.$isUser ? '300px' : 'auto'};
  height: auto;
  background: ${props => props.$isUser ? '#f5f5f5' : 'transparent'};
  border-radius: ${props => props.$isUser ? '24px' : '0'};
  padding: ${props => props.$isUser ? '14px 24px 13px' : '10px 0 0 8px'};
  border: ${props => props.$isUser ? '1px none #e0e0e0' : 'none'};

  @media (max-width: 1140px) and (min-width: 600px) {
   // max-width: 316px; 
   padding: ${props => props.$isUser ? '13px 24px' : '10px 0 0 8px'};
  }

  @media (max-width: 599px) {
   max-width: 274px;
   // padding: ${props => props.$isUser ? '12px 20px' : '10px 0 0 11px'};
   padding: ${props => props.$isUser ? '12px 20px' : '10px 0 0 1px'};
  }
`;

const AnswerChoices = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 360px;
  max-width: 100%;
  margin-top: 16px;
`;

const AnswerChoice = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 48px;
  padding: 11px 14px;
  border: 1px solid #e5e8eb;
  border-radius: 14px;
  background: #fff;
  color: rgba(0, 0, 0, 0.8);
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  letter-spacing: -0.28px;
  text-align: left;
  word-break: keep-all;
  overflow-wrap: anywhere;
  cursor: pointer;
  transition: border-color 120ms, background 120ms;

  @media (hover: hover) {
    &:hover:not(:disabled) { border-color: #1ae276; background: #f4fcf8; }
  }
  &:active:not(:disabled) { border-color: #1ae276; background: #eaf9f1; }
  &:focus-visible { outline: 2px solid #3a7dff; outline-offset: 3px; }
  &:disabled { opacity: 0.45; cursor: default; }
`;

const ChoiceNumber = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
  height: 24px;
  border-radius: 8px;
  background: #f5f6f7;
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
  font-weight: 600;
`;

const ChoiceAlternatives = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 16px;
`;

const AlternativeChoice = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 44px;
  padding: 4px 2px;
  border: 0;
  background: transparent;
  color: rgba(0, 0, 0, 0.55);
  font-family: Pretendard, sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 22px;
  cursor: pointer;

  &:hover:not(:disabled) { color: #146f54; }
  &:focus-visible { outline: 2px solid #3a7dff; outline-offset: 2px; border-radius: 4px; }
  &:disabled { opacity: 0.45; cursor: default; }
`;

const MessageText = styled.div`
  line-height: 1.4;
  word-break: keep-all;
  word-wrap: break-word;
  white-space: pre-line;
  color: rgba(0, 0, 0, 0.80);
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 26px;
  letter-spacing: -0.32px;

  /* 볼드체 스타일 */
  strong {
    font-weight: 700;
    color: rgba(0, 0, 0, 0.90);
  }

  @media (max-width: 599px) {
    font-size: 14px;
    letter-spacing: -0.28px;
    line-height: 23px; // (25-11-06) 모바일 행간은 임의설정함 피그마 위치를 못 찾겠어서
  }
`;

const UpdateButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 12px;
`;

const UpdateButton = styled.button`
  width: 148px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  border: 1px solid #1ae276;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    rgba(26, 226, 118, 0.05) 33.511%,
    rgba(28, 218, 129, 0.05) 37.873%,
    rgba(58, 125, 255, 0.05) 86.88%
  );
  color: rgba(0, 0, 0, 0.5);
  font-family: Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 26px;
  letter-spacing: -0.24px;
  cursor: pointer;
  padding: 4px 16px 4px 12px;
  white-space: nowrap;
  box-sizing: border-box;

  svg {
    flex: 0 0 auto;
  }
  
  &:disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  &:focus-visible {
    outline: 2px solid #00c968;
    outline-offset: 2px;
  }

  @media (hover: hover) {
    &:hover {
      opacity: 0.8;
    }
  }
`;

const PaidSearchButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 12px;
`;

const PaidSearchButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 132px;
  height: 36px;
  padding: 0 18px;
  border: none;
  border-radius: 999px;
  background: #292929;
  color: #fff;
  font-family: Pretendard;
  font-size: 13px;
  font-weight: 700;
  line-height: 20px;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;

  &:hover {
    opacity: 0.86;
  }

  &:active {
    transform: translateY(1px);
  }
  
  &:disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  @media (max-width: 599px) {
    height: 34px;
    min-width: 124px;
    padding: 0 16px;
    font-size: 12px;
  }
`;

const PrecedentGuideButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 12px;
`;

const PrecedentGuideButton = styled.button`
  display: inline-flex;
  padding: 4px 16px;
  align-items: center;
  gap: 7px;
  border-radius: 56px;
  border: 1px solid rgba(0, 0, 0, 0.10);
  background: #F8F8F8;
  color: rgba(0, 0, 0, 0.40);
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 26px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #EFEFEF;
    border-color: rgba(0, 0, 0, 0.15);
  }
  
  &:disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  @media (max-width: 599px) {
    font-size: 11px;
    padding: 3px 14px;
  }
`;

const PrecedentGuideButtonIcon = styled.img`
  width: 22px;
  height: 23px;
  display: block;
`;

const SearchButtonSlot = styled.div`
  width: 680px;
  min-height: 56px;
  flex-shrink: 0;

  @media (max-width: 1140px) and (min-width: 600px) {
    width: 536px;
  }

  @media (max-width: 599px) {
    width: calc(100% - 48px);
    min-height: 52px;
  }
`;

const SearchButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  height: 100%;
  flex-shrink: 0;
  padding: 8px 0;

  @media (max-width: 599px) {
    gap: 8px;
    padding: 8px 0;
  }
`;

const SearchNavLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  min-width: 0;
`;

const SearchNavRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
`;

const HistoryRoundNavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #f5f5f5;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.15s ease;

  &:disabled {
    opacity: 0.38;
    cursor: default;
  }

  @media (max-width: 599px) {
    width: 32px;
    height: 32px;
  }
`;

const HistoryStepText = styled.span`
  color: rgba(0, 0, 0, 0.6);
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.28px;
  white-space: nowrap;

  @media (max-width: 599px) {
    font-size: 12px;
    line-height: 20px;
    letter-spacing: -0.24px;
  }
`;

const ConfirmSearchText = styled.span`
  color: rgba(0, 0, 0, 0.6);
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.28px;
  white-space: nowrap;

  @media (max-width: 599px) {
    font-size: 12px;
    letter-spacing: -0.24px;
  }
`;

const CircleArrowButton = styled.button`
  width: 40px;
  height: 40px;
  border: none;
  background: url(${button}) no-repeat center center;
  background-size: cover;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.3);
    background-blend-mode: screen;
  }

  @media (max-width: 599px) {
    width: 36px;
    height: 36px;
  }
`;

const ErrorMessage = styled.span`
  font-weight: 700;
  color: #ff4444;
`;

const QueueMessage = styled.span`
  color: rgba(0, 0, 0, 0.55);
  font-weight: 600;
`;

// SSEProgressStack.jsx와 동일한 shimmer CSS 추가
const ShimmerText = styled.span`
  background: linear-gradient(
    120deg,
    rgba(180, 180, 180, 0.15) 0%,
    rgba(80, 80, 80, 0.85) 50%,
    rgba(180, 180, 180, 0.15) 100%
  );
  background-size: 200% auto;
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
  animation: shimmer 2s infinite linear;

  @keyframes shimmer {
    0% {
      background-position: 100% 100%;
    }
    100% {
      background-position: -100% 0;
    }
  }
`;

export default ChatContainer;
